// src/components/Laboratory/PathologyOrderQuestions.jsx
// DycoLIS Dynamic Ordering Requirements for Cytology, Hematopathology & Tissue Pathology
// Complies with official DycoLIS Ordering Requirements specifications

import React, { useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Stack,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  HelpOutline as HelpIcon,
  WarningAmber as WarningIcon,
} from "@mui/icons-material";

// Category definitions matching the PDF requirements
export const ORDERING_CATEGORIES = [
  { id: "GYN_CYTOLOGY", label: "Gynecologic Cytology", group: "Cytology", icon: "🔬" },
  { id: "NON_GYN_CYTOLOGY", label: "Non-Gynecologic Cytology", group: "Cytology", icon: "🧪" },
  { id: "FNA_CYTOLOGY", label: "Fine Needle Aspiration (FNA)", group: "Cytology", icon: "💉" },
  { id: "BONE_MARROW", label: "Bone Marrow Examination", group: "Hematopathology", icon: "🩸" },
  { id: "PERIPHERAL_BLOOD", label: "Peripheral Blood Hematopathology", group: "Hematopathology", icon: "🩸" },
  { id: "FLOW_CYTOMETRY", label: "Flow Cytometry", group: "Hematopathology", icon: "🧬" },
  { id: "CYTOGENETICS_MOLECULAR", label: "Cytogenetics / FISH / Molecular", group: "Hematopathology", icon: "🧬" },
  { id: "SURGICAL_PATHOLOGY", label: "Surgical Pathology / Biopsy", group: "Tissue Pathology", icon: "🧫" },
  { id: "RESECTION", label: "Resection Specimens", group: "Tissue Pathology", icon: "✂️" },
  { id: "BREAST_PATHOLOGY", label: "Breast Pathology", group: "Tissue Pathology", icon: "🎗️" },
  { id: "SPECIAL_STAINS_IHC", label: "Special Stains / IHC", group: "Tissue Pathology", icon: "🎨" },
  { id: "FROZEN_SECTION", label: "Frozen Section / Intraoperative", group: "Tissue Pathology", icon: "⏱️" },
];

/**
 * Helper to auto-detect category based on selected tests
 */
export const detectCategoriesFromTests = (tests = []) => {
  const detected = new Set();

  tests.forEach((t) => {
    const code = (t.testCode || "").toUpperCase();
    const name = (t.displayName || t.testName || t.name || "").toLowerCase();
    const spec = (t.specimenType || "").toLowerCase();
    const dept = (t.department?.code || t.departmentName || "").toUpperCase();

    // 1. Cytology
    if (
      code === "PAP_SMEAR" ||
      name.includes("pap smear") ||
      name.includes("cervical cytology") ||
      name.includes("thinprep") ||
      name.includes("lbc") ||
      name.includes("gynecologic cytology")
    ) {
      detected.add("GYN_CYTOLOGY");
    } else if (
      code === "FNAC" ||
      name.includes("fine needle") ||
      name.includes("fnac") ||
      name.includes("fna") ||
      name.includes("aspiration cytology")
    ) {
      detected.add("FNA_CYTOLOGY");
    } else if (
      code === "NON_GYN_CYTO" ||
      name.includes("sputum cytology") ||
      name.includes("urine cytology") ||
      name.includes("fluid cytology") ||
      name.includes("pleural fluid cytology") ||
      name.includes("ascitic fluid cytology") ||
      (name.includes("cytology") && !name.includes("cervical") && !name.includes("pap"))
    ) {
      detected.add("NON_GYN_CYTOLOGY");
    }
    // 2. Hematopathology
    else if (
      code === "BM_BX" ||
      name.includes("bone marrow") ||
      name.includes("bma") ||
      name.includes("trephine")
    ) {
      detected.add("BONE_MARROW");
    } else if (
      code === "PBF" ||
      name.includes("peripheral blood film") ||
      name.includes("blood film") ||
      name.includes("peripheral smear")
    ) {
      detected.add("PERIPHERAL_BLOOD");
    } else if (
      code === "FLOW_CYTO" ||
      name.includes("flow cytometry") ||
      name.includes("immunophenotyping") ||
      name.includes("cd4/cd8")
    ) {
      detected.add("FLOW_CYTOMETRY");
    } else if (
      code === "CYTOGENETICS" ||
      code === "FISH" ||
      name.includes("cytogenetics") ||
      name.includes("karyotype") ||
      name.includes("molecular hematopathology") ||
      name.includes("fish ")
    ) {
      detected.add("CYTOGENETICS_MOLECULAR");
    }
    // 3. Tissue Pathology
    else if (
      code === "BREAST_BX" ||
      name.includes("breast biopsy") ||
      name.includes("breast pathology") ||
      name.includes("lumpectomy")
    ) {
      detected.add("BREAST_PATHOLOGY");
    } else if (
      code === "FROZEN" ||
      name.includes("frozen section") ||
      name.includes("intraoperative consultation")
    ) {
      detected.add("FROZEN_SECTION");
    } else if (
      code === "IHC" ||
      code === "SPECIAL_STAINS" ||
      name.includes("ihc") ||
      name.includes("immunohistochemistry") ||
      name.includes("special stain")
    ) {
      detected.add("SPECIAL_STAINS_IHC");
    } else if (
      code === "RESECTION" ||
      name.includes("resection") ||
      name.includes("colectomy") ||
      name.includes("gastrectomy") ||
      name.includes("mastectomy")
    ) {
      detected.add("RESECTION");
    } else if (
      dept === "HISTO" ||
      dept === "HIST" ||
      spec === "tissue" ||
      spec === "aspirate" ||
      spec === "bone marrow" ||
      code === "TISSUE_GEN" ||
      code === "SKIN_BX" ||
      code === "GI_BX" ||
      code === "CERVIX_BX" ||
      code === "PROSTATE_BX" ||
      code === "LYMPH_BX" ||
      name.includes("biopsy") ||
      name.includes("histopathology")
    ) {
      detected.add("SURGICAL_PATHOLOGY");
    }
  });

  return Array.from(detected);
};

export const CATEGORY_SECTION_MAP = {
  GYN_CYTOLOGY: "gynCytology",
  NON_GYN_CYTOLOGY: "nonGynCytology",
  FNA_CYTOLOGY: "fnaCytology",
  BONE_MARROW: "boneMarrow",
  PERIPHERAL_BLOOD: "peripheralBlood",
  FLOW_CYTOMETRY: "flowCytometry",
  CYTOGENETICS_MOLECULAR: "cytogeneticsMolecular",
  SURGICAL_PATHOLOGY: "surgicalBiopsy",
  RESECTION: "resectionSpecimen",
  BREAST_PATHOLOGY: "breastPathology",
  SPECIAL_STAINS_IHC: "specialStainsIHC",
  FROZEN_SECTION: "frozenSection",
};

export const getCategorySectionKey = (catId) => CATEGORY_SECTION_MAP[catId] || null;

export const getDefaultsForCategory = (catId, tests = []) => {
  const nowStr = new Date().toISOString().slice(0, 16);
  const firstTestSpecimen = tests?.[0]?.specimenType || "";
  const firstTestName = tests?.[0]?.displayName || tests?.[0]?.testName || tests?.[0]?.name || "";

  switch (catId) {
    case "GYN_CYTOLOGY":
      return {
        specimenSource: "Cervical & Endocervical",
        collectionDevice: "Cervical Broom (Cervex-Brush)",
        collectionDateTime: nowStr,
        menstrualStatus: "Regular",
        pregnancyStatus: "Not Pregnant",
        menopauseStatus: "Premenopausal",
      };
    case "NON_GYN_CYTOLOGY":
      return {
        specimenType: firstTestSpecimen || "Urine",
        collectionMethod: "Spontaneous / Voided",
        collectionDateTime: nowStr,
        fixativePreservative: "95% Ethyl Alcohol",
        specimenVolume: "10 mL",
      };
    case "FNA_CYTOLOGY":
      return {
        collectionMethod: "Ultrasound-guided FNA",
        collectionDateTime: nowStr,
        containerType: "Glass Slides + CytoLyt",
        fixativePreservative: "95% Ethanol & Air-Dried",
        laterality: "N/A",
      };
    case "BONE_MARROW":
      return {
        specimenSourceSite: "Posterior Superior Iliac Crest",
        collectionDateTime: nowStr,
        containerType: "EDTA Tube + Formalin Trephine Container",
        anticoagulantFixative: "K2-EDTA (Aspirate) & 10% NBF (Trephine)",
        boneMarrowAspirate: "Diagnostic aspirate smears prepared bedside",
      };
    case "PERIPHERAL_BLOOD":
      return {
        specimenType: "Whole Blood (EDTA)",
        collectionDateTime: nowStr,
        collectionTubeAdditive: "K2-EDTA (Purple Top)",
        specimenVolume: "3 mL",
      };
    case "FLOW_CYTOMETRY":
      return {
        testPanelOrdered: "Leukemia / Lymphoma Panel",
        specimenType: firstTestSpecimen || "Bone Marrow Aspirate",
        collectionDateTime: nowStr,
        collectionTubeMedium: "Sodium Heparin (Green Top)",
        specimenVolume: "3 mL",
        transportStorageConditions: "Room Temperature (18-25°C), Do Not Freeze",
      };
    case "CYTOGENETICS_MOLECULAR":
      return {
        testStudyOrdered: "Chromosome Analysis (Karyotyping)",
        specimenType: firstTestSpecimen || "Bone Marrow Aspirate",
        collectionDateTime: nowStr,
        collectionTubeContainer: "Sodium Heparin Vacutainer",
        anticoagulantFixative: "Sodium Heparin (Green Top)",
        specimenVolume: "3-5 mL",
      };
    case "SURGICAL_PATHOLOGY":
      return {
        specimenType: firstTestSpecimen || "Biopsy",
        collectionDateTime: nowStr,
        numberOfContainers: 1,
        fixative: "10% Neutral Buffered Formalin",
      };
    case "RESECTION":
      return {
        specimenType: "Resection",
        collectionDateTime: nowStr,
        numberOfContainers: 1,
        fixative: "10% Neutral Buffered Formalin",
      };
    case "BREAST_PATHOLOGY":
      return {
        specimenType: "Core Needle Biopsy",
        laterality: "Right",
        fixative: "10% Neutral Buffered Formalin (ASCO/CAP compliant)",
      };
    case "SPECIAL_STAINS_IHC":
      return {
        testStainRequested: firstTestName || "Diagnostic IHC Marker Panel",
        requestedMarkerStain: firstTestName || "IHC Marker",
        specimenType: "FFPE Block / Unstained Slides",
        collectionDateTime: nowStr,
      };
    case "FROZEN_SECTION":
      return {
        specimenType: "Fresh Tissue (NO Fixative)",
        intraoperativeUrgency: "Immediate intraoperative evaluation (15-20 min)",
        collectionDateTime: nowStr,
      };
    default:
      return {};
  }
};

export default function PathologyOrderQuestions({
  isDark,
  selectedTests = [],
  clinicalRequirements = {},
  onChange,
  validationErrors = [],
}) {
  const textPrimary = isDark ? "#F8FAFC" : "#0F172A";
  const textSecondary = isDark ? "#94A3B8" : "#64748B";
  const cardBg = isDark ? "#1E1E1E" : "#FFFFFF";
  const innerCardBg = isDark ? "#141414" : "#F8FAFC";
  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "#E2E8F0";
  const compulsoryColor = "#EF4444";

  // Auto-detect categories from currently selected tests
  const detectedCategories = useMemo(
    () => detectCategoriesFromTests(selectedTests),
    [selectedTests]
  );

  const activeCategory =
    (clinicalRequirements.category && detectedCategories.includes(clinicalRequirements.category))
      ? clinicalRequirements.category
      : (detectedCategories[0] || null);

  // Synchronize active category with detected categories and initialize default values
  useEffect(() => {
    if (detectedCategories.length === 0) {
      if (clinicalRequirements.category) {
        onChange((prev) => {
          const next = { ...prev };
          delete next.category;
          return next;
        });
      }
    } else {
      const targetCat =
        clinicalRequirements.category && detectedCategories.includes(clinicalRequirements.category)
          ? clinicalRequirements.category
          : detectedCategories[0];

      const sectionKey = getCategorySectionKey(targetCat);
      const defaults = getDefaultsForCategory(targetCat, selectedTests);

      onChange((prev) => {
        const next = { ...prev, category: targetCat };
        if (sectionKey && defaults) {
          const currentSection = next[sectionKey] || {};
          let changed = false;
          const mergedSection = { ...currentSection };
          Object.keys(defaults).forEach((key) => {
            if (mergedSection[key] === undefined || mergedSection[key] === null || mergedSection[key] === "") {
              mergedSection[key] = defaults[key];
              changed = true;
            }
          });
          if (changed || next.category !== prev.category) {
            next[sectionKey] = mergedSection;
            return next;
          }
        }
        if (next.category !== prev.category) return next;
        return prev;
      });
    }
  }, [detectedCategories, clinicalRequirements.category, selectedTests, onChange]);

  // Handler for category switch
  const handleSelectCategory = (catId) => {
    const sectionKey = getCategorySectionKey(catId);
    const defaults = getDefaultsForCategory(catId, selectedTests);
    onChange((prev) => {
      const next = { ...prev, category: catId };
      if (sectionKey && defaults) {
        next[sectionKey] = {
          ...defaults,
          ...(prev[sectionKey] || {}),
        };
      }
      return next;
    });
  };

  // Generic field updater for sub-questionnaires
  const updateSectionField = (sectionKey, fieldKey, value) => {
    onChange((prev) => ({
      ...prev,
      [sectionKey]: {
        ...(prev?.[sectionKey] || {}),
        [fieldKey]: value,
      },
    }));
  };

  // Multiple specimens management
  const specimensList = clinicalRequirements.specimens || [];

  const handleAddSpecimenContainer = () => {
    const nextIdx = specimensList.length + 1;
    const letter = String.fromCharCode(64 + nextIdx);
    const newSpecimen = {
      specimenIndex: nextIdx,
      containerLabel: `Specimen ${letter} / Jar ${nextIdx}`,
      specimenType: "Tissue",
      anatomicSite: "",
      collectionDateTime: new Date().toISOString().slice(0, 16),
      fixative: "10% Neutral Buffered Formalin",
      volume: "1 container",
      notes: "",
    };
    onChange({
      ...clinicalRequirements,
      specimens: [...specimensList, newSpecimen],
    });
  };

  const handleRemoveSpecimenContainer = (index) => {
    const updated = specimensList.filter((_, i) => i !== index);
    onChange({
      ...clinicalRequirements,
      specimens: updated,
    });
  };

  const handleUpdateSpecimenField = (index, field, value) => {
    const updated = [...specimensList];
    updated[index] = { ...updated[index], [field]: value };
    onChange({
      ...clinicalRequirements,
      specimens: updated,
    });
  };

  // Helper to render field label with clean compulsory red asterisk / optional badge
  const renderLabel = (label, isCompulsory = false, tooltipText = "") => {
    const isError = Boolean(
      validationErrors &&
      validationErrors.length > 0 &&
      validationErrors.some((err) => {
        const cleanErr = err.toLowerCase().replace(/[^a-z0-9]/g, "");
        const cleanLbl = (label || "").toLowerCase().replace(/[^a-z0-9]/g, "");
        return cleanErr.includes(cleanLbl) || cleanLbl.includes(cleanErr);
      })
    );

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 0.25 }} data-field-name={label}>
        <Typography
          sx={{
            fontFamily: "'Satoshi', sans-serif",
            fontSize: "0.72rem",
            fontWeight: 700,
            color: isError ? "#EF4444" : textPrimary,
          }}
        >
          {label}
          {isCompulsory && (
            <Box component="span" sx={{ color: "#EF4444", ml: 0.3, fontWeight: "bold", fontSize: '0.78rem' }}>
              *
            </Box>
          )}
        </Typography>
        {!isCompulsory && (
          <Chip
            label="Optional"
            size="small"
            sx={{
              height: 14,
              fontSize: "0.58rem",
              fontWeight: 500,
              bgcolor: isDark ? "rgba(255, 255, 255, 0.05)" : "#F1F5F9",
              color: textSecondary,
              px: 0.2,
            }}
          />
        )}
        {tooltipText && (
          <Tooltip title={tooltipText} arrow>
            <HelpIcon sx={{ fontSize: 12, color: textSecondary, cursor: "pointer" }} />
          </Tooltip>
        )}
      </Box>
    );
  };

  const inputStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px",
      fontFamily: "'Satoshi', sans-serif",
      fontSize: "0.75rem",
      bgcolor: isDark ? "#141414" : "#FFFFFF",
      "& fieldset": { borderColor },
      "&:hover fieldset": { borderColor: "#3B82F6" },
      "&.Mui-focused fieldset": { borderColor: "#2563EB" },
    },
    "& .MuiInputBase-input": {
      color: textPrimary,
      py: "4px !important",
      px: "8px !important",
      fontSize: "0.75rem",
      height: "18px",
    },
    "& .MuiSelect-select": {
      py: "4px !important",
      px: "8px !important",
      fontSize: "0.75rem",
      minHeight: "18px !important",
      lineHeight: "18px",
      display: "flex",
      alignItems: "center",
    },
    "& .MuiInputBase-inputMultiline": {
      height: "auto !important",
      py: "4px !important",
      px: "8px !important",
      fontSize: "0.75rem",
    },
  };

  // If no pathology/cytology/hematopathology tests are detected, do NOT render this section at all!
  if (detectedCategories.length === 0 || !activeCategory) {
    return null;
  }

  // Filter available tabs strictly to detected categories
  const availableCategories = ORDERING_CATEGORIES.filter((c) =>
    detectedCategories.includes(c.id)
  );

  // Active Category Meta
  const currentCatMeta =
    ORDERING_CATEGORIES.find((c) => c.id === activeCategory) ||
    availableCategories[0] ||
    ORDERING_CATEGORIES[0];

  return (
    <Paper
      id="pathology-ordering-questions"
      elevation={0}
      sx={{
        p: 1.5,
        borderRadius: "10px",
        bgcolor: cardBg,
        border: `1px solid ${isDark ? "rgba(59, 130, 246, 0.3)" : "#BFDBFE"}`,
        boxShadow: isDark
          ? "0 2px 10px rgba(0,0,0,0.3)"
          : "0 2px 10px rgba(37, 99, 235, 0.04)",
        mb: 1.5,
        position: "relative",
      }}
    >
      {/* Header Badge & Title */}
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", mb: 1.25, gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 26,
              height: 26,
              borderRadius: "6px",
              bgcolor: isDark ? "rgba(37, 99, 235, 0.2)" : "#EFF6FF",
              color: "#2563EB",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.95rem",
            }}
          >
            {currentCatMeta.icon}
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: "'Satoshi', sans-serif",
                fontSize: "0.85rem",
                fontWeight: 700,
                color: textPrimary,
                lineHeight: 1.2,
              }}
            >
              Pathology & Cytology Ordering Requirements
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Satoshi', sans-serif",
                fontSize: "0.68rem",
                color: textSecondary,
                mt: 0.1,
              }}
            >
              Category: <b>{currentCatMeta.group}</b> — {currentCatMeta.label}
            </Typography>
          </Box>
        </Box>

        <Chip
          label="DycoLIS Standards Compliant"
          size="small"
          color="primary"
          variant="outlined"
          sx={{ fontWeight: 600, fontSize: "0.62rem", height: 20 }}
        />
      </Box>

      {/* Notice if multiple categories detected */}
      {detectedCategories.length > 1 && (
        <Alert severity="info" sx={{ mb: 1.5, borderRadius: "6px", py: 0.25, px: 1, fontSize: "0.72rem" }}>
          Multiple pathology categories detected from your selected tests. Please review questions for each test type using the tabs below.
        </Alert>
      )}

      {/* Category Tabs / Switcher (only shown if multiple pathology categories detected) */}
      {availableCategories.length > 1 && (
        <Box sx={{ borderBottom: `1px solid ${borderColor}`, mb: 1.5 }}>
          <Tabs
            value={activeCategory}
            onChange={(_, val) => handleSelectCategory(val)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 32,
              "& .MuiTab-root": {
                fontFamily: "'Satoshi', sans-serif",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.74rem",
                minHeight: 32,
                py: 0.5,
                px: 1.25,
                color: textSecondary,
                "&.Mui-selected": {
                  color: "#2563EB",
                },
              },
              "& .MuiTabs-indicator": {
                bgcolor: "#2563EB",
                height: 2.5,
                borderRadius: "2px 2px 0 0",
              },
            }}
          >
            {availableCategories.map((cat) => (
              <Tab
                key={cat.id}
                value={cat.id}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                    <span>{cat.icon}</span>
                    <span>{cat.label}</span>
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>
      )}

      {/* ──────────────────────────────────────────────────────────── */}
      {/* 1. CYTOLOGY SECTION                                          */}
      {/* ──────────────────────────────────────────────────────────── */}

      {/* 1A. Gynecologic Cytology */}
      {activeCategory === "GYN_CYTOLOGY" && (
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: textPrimary, mb: 1 }}>
            Gynecologic Cytology Questionnaire (Pap Smear / Cervical / Vaginal)
          </Typography>

          <Grid container spacing={1.25}>
            {/* Specimen Source */}
            <Grid item xs={12} md={6}>
              {renderLabel("Specimen Source", true, "Cervical, endocervical, vaginal or other site")}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.gynCytology?.specimenSource || "Cervical & Endocervical"}
                  onChange={(e) => updateSectionField("gynCytology", "specimenSource", e.target.value)}
                >
                  <MenuItem value="Cervical">Cervical</MenuItem>
                  <MenuItem value="Endocervical">Endocervical</MenuItem>
                  <MenuItem value="Vaginal">Vaginal</MenuItem>
                  <MenuItem value="Cervical & Endocervical">Cervical & Endocervical (Transformation Zone)</MenuItem>
                  <MenuItem value="Other gynecologic site">Other Gynecologic Site</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Collection Method / Device */}
            <Grid item xs={12} md={6}>
              {renderLabel("Collection Method / Device", true, "Sampling device used")}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.gynCytology?.collectionDevice || "Cervical Broom (Cervex-Brush)"}
                  onChange={(e) => updateSectionField("gynCytology", "collectionDevice", e.target.value)}
                >
                  <MenuItem value="Cervical Broom (Cervex-Brush)">Cervical Broom (Cervex-Brush)</MenuItem>
                  <MenuItem value="Ayre Spatula + Endocervical Brush">Ayre Spatula + Endocervical Brush</MenuItem>
                  <MenuItem value="Cotton Swab">Cotton Swab</MenuItem>
                  <MenuItem value="Liquid-Based Cytology (ThinPrep / SurePath)">Liquid-Based Cytology Vial</MenuItem>
                  <MenuItem value="Conventional Glass Slide Smear">Conventional Glass Slide Smear</MenuItem>
                  <MenuItem value="Other">Other Device</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Date / Time of Specimen Collection */}
            <Grid item xs={12} md={6}>
              {renderLabel("Date/Time of Specimen Collection", false)}
              <TextField
                fullWidth
                size="small"
                type="datetime-local"
                value={
                  clinicalRequirements.gynCytology?.collectionDateTime ||
                  new Date().toISOString().slice(0, 16)
                }
                onChange={(e) => updateSectionField("gynCytology", "collectionDateTime", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            {/* LMP */}
            <Grid item xs={12} md={6}>
              {renderLabel("LMP (Last Menstrual Period)", false)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. 14 days ago, or YYYY-MM-DD"
                value={clinicalRequirements.gynCytology?.lmp || ""}
                onChange={(e) => updateSectionField("gynCytology", "lmp", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            {/* Menstrual & Menopause Status */}
            <Grid item xs={12} md={4}>
              {renderLabel("Menstrual Status", false)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.gynCytology?.menstrualStatus || "Regular"}
                  onChange={(e) => updateSectionField("gynCytology", "menstrualStatus", e.target.value)}
                >
                  <MenuItem value="Regular">Regular Cyclic</MenuItem>
                  <MenuItem value="Irregular">Irregular</MenuItem>
                  <MenuItem value="Amenorrheic">Amenorrheic</MenuItem>
                  <MenuItem value="Post-Hysterectomy">Post-Hysterectomy</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Pregnancy / Postpartum Status", false)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.gynCytology?.pregnancyStatus || "Not Pregnant"}
                  onChange={(e) => updateSectionField("gynCytology", "pregnancyStatus", e.target.value)}
                >
                  <MenuItem value="Not Pregnant">Not Pregnant</MenuItem>
                  <MenuItem value="Pregnant (1st Trimester)">Pregnant (1st Trimester)</MenuItem>
                  <MenuItem value="Pregnant (2nd Trimester)">Pregnant (2nd Trimester)</MenuItem>
                  <MenuItem value="Pregnant (3rd Trimester)">Pregnant (3rd Trimester)</MenuItem>
                  <MenuItem value="Postpartum / Lactating">Postpartum / Lactating</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Menopause Status", false)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.gynCytology?.menopauseStatus || "Premenopausal"}
                  onChange={(e) => updateSectionField("gynCytology", "menopauseStatus", e.target.value)}
                >
                  <MenuItem value="Premenopausal">Premenopausal</MenuItem>
                  <MenuItem value="Perimenopausal">Perimenopausal</MenuItem>
                  <MenuItem value="Postmenopausal">Postmenopausal</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Hormone Therapy & History */}
            <Grid item xs={12} md={6}>
              {renderLabel("Hormone Therapy Status", false)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Oral contraceptives, HRT, Tamoxifen, IUD"
                value={clinicalRequirements.gynCytology?.hormoneTherapy || ""}
                onChange={(e) => updateSectionField("gynCytology", "hormoneTherapy", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {renderLabel("Previous Abnormal Cervical Cytology", false)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. ASC-US in 2023, LSIL, None"
                value={clinicalRequirements.gynCytology?.previousAbnormalCytology || ""}
                onChange={(e) => updateSectionField("gynCytology", "previousAbnormalCytology", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {renderLabel("Previous Biopsy / Surgical Procedure", false)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Cervical biopsy, LEEP, Cryo, Cone biopsy, Hysterectomy"
                value={clinicalRequirements.gynCytology?.previousBiopsyOrSurgery || ""}
                onChange={(e) => updateSectionField("gynCytology", "previousBiopsyOrSurgery", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            {/* Compulsory Clinical History */}
            <Grid item xs={12} md={6}>
              {renderLabel("Relevant Clinical History", true)}
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="Indication: Routine screening, post-coital bleeding, discharge, surveillance..."
                value={clinicalRequirements.gynCytology?.clinicalHistory || ""}
                onChange={(e) => updateSectionField("gynCytology", "clinicalHistory", e.target.value)}
                sx={inputStyles}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* 1B. Non-Gynecologic Cytology */}
      {activeCategory === "NON_GYN_CYTOLOGY" && (
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: textPrimary, mb: 1 }}>
            Non-Gynecologic Cytology Questionnaire (Urine, Sputum, Body Fluids, Washings)
          </Typography>

          <Grid container spacing={1.25}>
            <Grid item xs={12} md={4}>
              {renderLabel("Specimen Type", true)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.nonGynCytology?.specimenType || "Urine"}
                  onChange={(e) => updateSectionField("nonGynCytology", "specimenType", e.target.value)}
                >
                  <MenuItem value="Urine">Urine</MenuItem>
                  <MenuItem value="Sputum">Sputum</MenuItem>
                  <MenuItem value="Pleural Fluid">Pleural Fluid</MenuItem>
                  <MenuItem value="Peritoneal / Ascitic Fluid">Peritoneal / Ascitic Fluid</MenuItem>
                  <MenuItem value="Pericardial Fluid">Pericardial Fluid</MenuItem>
                  <MenuItem value="Cerebrospinal Fluid (CSF)">Cerebrospinal Fluid (CSF)</MenuItem>
                  <MenuItem value="Synovial Fluid">Synovial Fluid</MenuItem>
                  <MenuItem value="Bronchial Washing / Brushing">Bronchial Washing / Brushing</MenuItem>
                  <MenuItem value="Other Fluid">Other Body Fluid</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Specific Anatomic Source / Site", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Right pleural cavity, Bladder voided, Left bronchus"
                value={clinicalRequirements.nonGynCytology?.anatomicSourceSite || ""}
                onChange={(e) => updateSectionField("nonGynCytology", "anatomicSourceSite", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Collection Method", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Thoracentesis, Voided clean catch, Bronchoscopy, Tap"
                value={clinicalRequirements.nonGynCytology?.collectionMethod || ""}
                onChange={(e) => updateSectionField("nonGynCytology", "collectionMethod", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Date/Time of Collection", false)}
              <TextField
                fullWidth
                size="small"
                type="datetime-local"
                value={
                  clinicalRequirements.nonGynCytology?.collectionDateTime ||
                  new Date().toISOString().slice(0, 16)
                }
                onChange={(e) => updateSectionField("nonGynCytology", "collectionDateTime", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Fixative / Preservative", true)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.nonGynCytology?.fixativePreservative || "95% Ethyl Alcohol"}
                  onChange={(e) => updateSectionField("nonGynCytology", "fixativePreservative", e.target.value)}
                >
                  <MenuItem value="95% Ethyl Alcohol">95% Ethyl Alcohol</MenuItem>
                  <MenuItem value="CytoLyt / PreservCyt">CytoLyt / PreservCyt</MenuItem>
                  <MenuItem value="Saccomanno Fixative">Saccomanno Fixative</MenuItem>
                  <MenuItem value="Fresh (Unfixed on Ice)">Fresh (Unfixed on Ice)</MenuItem>
                  <MenuItem value="10% Neutral Buffered Formalin">10% Neutral Buffered Formalin</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Specimen Volume", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. 50 mL, 200 mL, 5 mL"
                value={clinicalRequirements.nonGynCytology?.specimenVolume || ""}
                onChange={(e) => updateSectionField("nonGynCytology", "specimenVolume", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {renderLabel("Relevant Clinical History", true)}
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="Clinical symptoms, history of malignancy, infection suspicion..."
                value={clinicalRequirements.nonGynCytology?.clinicalHistory || ""}
                onChange={(e) => updateSectionField("nonGynCytology", "clinicalHistory", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              {renderLabel("Lesion / Mass Information", false)}
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="Mass size, location, consistency..."
                value={clinicalRequirements.nonGynCytology?.lesionMassInfo || ""}
                onChange={(e) => updateSectionField("nonGynCytology", "lesionMassInfo", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              {renderLabel("Relevant Imaging Findings", false)}
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="CT / X-ray / Ultrasound correlation..."
                value={clinicalRequirements.nonGynCytology?.imagingFindings || ""}
                onChange={(e) => updateSectionField("nonGynCytology", "imagingFindings", e.target.value)}
                sx={inputStyles}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* 1C. Fine Needle Aspiration (FNA) */}
      {activeCategory === "FNA_CYTOLOGY" && (
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: textPrimary, mb: 1 }}>
            Fine Needle Aspiration (FNA) Cytology Questionnaire
          </Typography>

          <Grid container spacing={1.25}>
            <Grid item xs={12} md={4}>
              {renderLabel("Anatomic Site", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Thyroid, Breast, Cervical lymph node, Parotid"
                value={clinicalRequirements.fnaCytology?.anatomicSite || ""}
                onChange={(e) => updateSectionField("fnaCytology", "anatomicSite", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Laterality", false)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.fnaCytology?.laterality || "N/A"}
                  onChange={(e) => updateSectionField("fnaCytology", "laterality", e.target.value)}
                >
                  <MenuItem value="Left">Left</MenuItem>
                  <MenuItem value="Right">Right</MenuItem>
                  <MenuItem value="Bilateral">Bilateral</MenuItem>
                  <MenuItem value="Midline">Midline</MenuItem>
                  <MenuItem value="N/A">N/A</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Collection Method", true)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.fnaCytology?.collectionMethod || "Ultrasound-guided FNA"}
                  onChange={(e) => updateSectionField("fnaCytology", "collectionMethod", e.target.value)}
                >
                  <MenuItem value="Palpation-guided FNA">Palpation-guided FNA</MenuItem>
                  <MenuItem value="Ultrasound-guided FNA">Ultrasound-guided FNA</MenuItem>
                  <MenuItem value="CT-guided FNA">CT-guided FNA</MenuItem>
                  <MenuItem value="EUS-guided FNA">EUS-guided (Endoscopic Ultrasound) FNA</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Date/Time of Collection", false)}
              <TextField
                fullWidth
                size="small"
                type="datetime-local"
                value={
                  clinicalRequirements.fnaCytology?.collectionDateTime ||
                  new Date().toISOString().slice(0, 16)
                }
                onChange={(e) => updateSectionField("fnaCytology", "collectionDateTime", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Number of Passes / Specimens", false)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. 3 passes, 2 passes"
                value={clinicalRequirements.fnaCytology?.numberOfPasses || ""}
                onChange={(e) => updateSectionField("fnaCytology", "numberOfPasses", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Specimen / Container Type", true)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.fnaCytology?.containerType || "Glass Slides + CytoLyt"}
                  onChange={(e) => updateSectionField("fnaCytology", "containerType", e.target.value)}
                >
                  <MenuItem value="Glass Slides + CytoLyt">Glass Slides + CytoLyt Vial</MenuItem>
                  <MenuItem value="Direct Smears on Slides">Direct Smears on Slides (Air-dried & Fixed)</MenuItem>
                  <MenuItem value="CytoLyt Vial (Liquid-based)">CytoLyt Vial (Liquid-based)</MenuItem>
                  <MenuItem value="Formalin Vial (Cell block)">Formalin Vial (Cell Block)</MenuItem>
                  <MenuItem value="RPMI Medium (Flow/Molecular)">RPMI Medium (Flow/Molecular)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Fixative / Preservative", true)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.fnaCytology?.fixativePreservative || "95% Ethanol & Air-Dried"}
                  onChange={(e) => updateSectionField("fnaCytology", "fixativePreservative", e.target.value)}
                >
                  <MenuItem value="95% Ethanol & Air-Dried">95% Ethanol & Air-Dried (Diff-Quik)</MenuItem>
                  <MenuItem value="95% Ethanol only">95% Ethanol only (Pap)</MenuItem>
                  <MenuItem value="Air-dried only">Air-dried only (Giemsa/Diff-Quik)</MenuItem>
                  <MenuItem value="10% Neutral Buffered Formalin">10% Neutral Buffered Formalin (Cell Block)</MenuItem>
                  <MenuItem value="CytoLyt solution">CytoLyt Solution</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Clinical Indication", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Solitary thyroid nodule, lymphadenopathy"
                value={clinicalRequirements.fnaCytology?.clinicalIndication || ""}
                onChange={(e) => updateSectionField("fnaCytology", "clinicalIndication", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Relevant Imaging Findings", false)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. TI-RADS 4B nodule 18mm, BI-RADS 4"
                value={clinicalRequirements.fnaCytology?.imagingFindings || ""}
                onChange={(e) => updateSectionField("fnaCytology", "imagingFindings", e.target.value)}
                sx={inputStyles}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* ──────────────────────────────────────────────────────────── */}
      {/* 2. HEMATOPATHOLOGY SECTION                                   */}
      {/* ──────────────────────────────────────────────────────────── */}

      {/* 2A. Bone Marrow Examination */}
      {activeCategory === "BONE_MARROW" && (
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: textPrimary, mb: 1 }}>
            Bone Marrow Examination Questionnaire (Aspirate & Trephine Biopsy)
          </Typography>

          <Grid container spacing={1.25}>
            <Grid item xs={12} md={4}>
              {renderLabel("Specimen Source / Site", true)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.boneMarrow?.specimenSourceSite || "Posterior Superior Iliac Crest"}
                  onChange={(e) => updateSectionField("boneMarrow", "specimenSourceSite", e.target.value)}
                >
                  <MenuItem value="Posterior Superior Iliac Crest">Posterior Superior Iliac Crest (PSIS)</MenuItem>
                  <MenuItem value="Anterior Iliac Crest">Anterior Iliac Crest</MenuItem>
                  <MenuItem value="Sternum">Sternum</MenuItem>
                  <MenuItem value="Tibia (Pediatric)">Tibia (Pediatric)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Date/Time of Collection", false)}
              <TextField
                fullWidth
                size="small"
                type="datetime-local"
                value={
                  clinicalRequirements.boneMarrow?.collectionDateTime ||
                  new Date().toISOString().slice(0, 16)
                }
                onChange={(e) => updateSectionField("boneMarrow", "collectionDateTime", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Bone Marrow Aspirate", true, "Aspirate details / volume")}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. 2 mL aspirate obtained, particles visible, dry tap"
                value={clinicalRequirements.boneMarrow?.boneMarrowAspirate || ""}
                onChange={(e) => updateSectionField("boneMarrow", "boneMarrowAspirate", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Specimen / Container Type", true)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.boneMarrow?.containerType || "EDTA Tube + Formalin Trephine Container"}
                  onChange={(e) => updateSectionField("boneMarrow", "containerType", e.target.value)}
                >
                  <MenuItem value="EDTA Tube + Formalin Trephine Container">EDTA Tube + Formalin Container</MenuItem>
                  <MenuItem value="Heparin Tube + Formalin Trephine Container">Heparin Tube + Formalin Container</MenuItem>
                  <MenuItem value="Glass Slides + Formalin Container">Glass Slides + Formalin Container</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Anticoagulant / Fixative", true)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.boneMarrow?.anticoagulantFixative || "K2-EDTA (Aspirate) & 10% NBF (Trephine)"}
                  onChange={(e) => updateSectionField("boneMarrow", "anticoagulantFixative", e.target.value)}
                >
                  <MenuItem value="K2-EDTA (Aspirate) & 10% NBF (Trephine)">K2-EDTA (Aspirate) & 10% NBF (Trephine)</MenuItem>
                  <MenuItem value="Sodium Heparin (Flow/Cyto) & Formalin">Sodium Heparin (Flow/Cyto) & Formalin</MenuItem>
                  <MenuItem value="Zinc Formalin">Zinc Formalin (Enhanced nuclear detail)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Clinical Indication", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Unexplained pancytopenia, leukemia staging"
                value={clinicalRequirements.boneMarrow?.clinicalIndication || ""}
                onChange={(e) => updateSectionField("boneMarrow", "clinicalIndication", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {renderLabel("Relevant Clinical History", true)}
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="Constitutional symptoms, prior chemotherapy, transfusion history..."
                value={clinicalRequirements.boneMarrow?.clinicalHistory || ""}
                onChange={(e) => updateSectionField("boneMarrow", "clinicalHistory", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {renderLabel("Relevant CBC / Differential Results", false)}
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="e.g. Hb 7.4 g/dL, WBC 1.9 x10^9/L (Blasts 4%), Plt 38 x10^9/L"
                value={clinicalRequirements.boneMarrow?.cbcDifferentialResults || ""}
                onChange={(e) => updateSectionField("boneMarrow", "cbcDifferentialResults", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              {renderLabel("Suspected Diagnosis", false)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. AML, Multiple Myeloma, MDS"
                value={clinicalRequirements.boneMarrow?.suspectedDiagnosis || ""}
                onChange={(e) => updateSectionField("boneMarrow", "suspectedDiagnosis", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              {renderLabel("Bone Marrow Core Biopsy", false)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. 1.8 cm trephine core obtained"
                value={clinicalRequirements.boneMarrow?.boneMarrowCoreBiopsy || ""}
                onChange={(e) => updateSectionField("boneMarrow", "boneMarrowCoreBiopsy", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              {renderLabel("Bone Marrow Clot", false)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Submitted in formalin container"
                value={clinicalRequirements.boneMarrow?.boneMarrowClot || ""}
                onChange={(e) => updateSectionField("boneMarrow", "boneMarrowClot", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              {renderLabel("Required Ancillary Studies", false)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Flow cytometry, Cytogenetics, Iron stain"
                value={clinicalRequirements.boneMarrow?.ancillaryStudies || ""}
                onChange={(e) => updateSectionField("boneMarrow", "ancillaryStudies", e.target.value)}
                sx={inputStyles}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* 2B. Peripheral Blood Hematopathology */}
      {activeCategory === "PERIPHERAL_BLOOD" && (
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: textPrimary, mb: 1 }}>
            Peripheral Blood Hematopathology Questionnaire (PBF / Blood Film)
          </Typography>

          <Grid container spacing={1.25}>
            <Grid item xs={12} md={4}>
              {renderLabel("Specimen Type", true)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.peripheralBlood?.specimenType || "Whole Blood (EDTA)"}
                  onChange={(e) => updateSectionField("peripheralBlood", "specimenType", e.target.value)}
                >
                  <MenuItem value="Whole Blood (EDTA)">Whole Blood (EDTA)</MenuItem>
                  <MenuItem value="Direct Fingerstick Smears">Direct Fingerstick Smears</MenuItem>
                  <MenuItem value="Citrated Blood">Citrated Blood</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Collection Tube / Additive", true)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.peripheralBlood?.collectionTubeAdditive || "K2-EDTA (Purple Top)"}
                  onChange={(e) => updateSectionField("peripheralBlood", "collectionTubeAdditive", e.target.value)}
                >
                  <MenuItem value="K2-EDTA (Purple Top)">K2-EDTA (Purple Top)</MenuItem>
                  <MenuItem value="Sodium Citrate (Blue Top)">Sodium Citrate (Blue Top)</MenuItem>
                  <MenuItem value="Sodium Heparin (Green Top)">Sodium Heparin (Green Top)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Specimen Volume", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. 3 mL, 4 mL"
                value={clinicalRequirements.peripheralBlood?.specimenVolume || "3 mL"}
                onChange={(e) => updateSectionField("peripheralBlood", "specimenVolume", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Date/Time of Collection", false)}
              <TextField
                fullWidth
                size="small"
                type="datetime-local"
                value={
                  clinicalRequirements.peripheralBlood?.collectionDateTime ||
                  new Date().toISOString().slice(0, 16)
                }
                onChange={(e) => updateSectionField("peripheralBlood", "collectionDateTime", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Clinical Indication", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Leukocytosis, suspected hemolysis, thrombocytosis"
                value={clinicalRequirements.peripheralBlood?.clinicalIndication || ""}
                onChange={(e) => updateSectionField("peripheralBlood", "clinicalIndication", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Smear Requirements", false)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Manual differential, malaria scan, reticulocyte"
                value={clinicalRequirements.peripheralBlood?.smearRequirements || ""}
                onChange={(e) => updateSectionField("peripheralBlood", "smearRequirements", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {renderLabel("Relevant Clinical History", true)}
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="Fever, bleeding, lymphadenopathy, splenomegaly, recent travel..."
                value={clinicalRequirements.peripheralBlood?.clinicalHistory || ""}
                onChange={(e) => updateSectionField("peripheralBlood", "clinicalHistory", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {renderLabel("Relevant CBC / Differential", false)}
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="e.g. WBC 32.4, Platelets 650, Hb 10.1..."
                value={clinicalRequirements.peripheralBlood?.cbcDifferential || ""}
                onChange={(e) => updateSectionField("peripheralBlood", "cbcDifferential", e.target.value)}
                sx={inputStyles}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* 2C. Flow Cytometry */}
      {activeCategory === "FLOW_CYTOMETRY" && (
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: textPrimary, mb: 1 }}>
            Flow Cytometry Ordering Questionnaire
          </Typography>

          <Grid container spacing={1.25}>
            <Grid item xs={12} md={4}>
              {renderLabel("Test / Panel Ordered", true)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.flowCytometry?.testPanelOrdered || "Leukemia / Lymphoma Panel"}
                  onChange={(e) => updateSectionField("flowCytometry", "testPanelOrdered", e.target.value)}
                >
                  <MenuItem value="Leukemia / Lymphoma Panel">Leukemia / Lymphoma Immunophenotyping Panel</MenuItem>
                  <MenuItem value="CD4 / CD8 T-Cell Subsets">CD4 / CD8 T-Cell Subsets</MenuItem>
                  <MenuItem value="Multiple Myeloma Plasma Cell Panel">Multiple Myeloma Plasma Cell Panel</MenuItem>
                  <MenuItem value="PNH Screen (CD55/CD59)">PNH Screen (CD55 / CD59)</MenuItem>
                  <MenuItem value="Minimal Residual Disease (MRD)">Minimal Residual Disease (MRD)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Specimen Type", true)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.flowCytometry?.specimenType || "Bone Marrow Aspirate"}
                  onChange={(e) => updateSectionField("flowCytometry", "specimenType", e.target.value)}
                >
                  <MenuItem value="Bone Marrow Aspirate">Bone Marrow Aspirate</MenuItem>
                  <MenuItem value="Peripheral Blood">Peripheral Blood</MenuItem>
                  <MenuItem value="Lymph Node Cell Suspension">Lymph Node Cell Suspension</MenuItem>
                  <MenuItem value="Body Fluid / CSF">Body Fluid / CSF</MenuItem>
                  <MenuItem value="Fine Needle Aspirate">Fine Needle Aspirate</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Specimen Source / Site", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Posterior iliac crest, Venous blood, Left neck node"
                value={clinicalRequirements.flowCytometry?.specimenSourceSite || ""}
                onChange={(e) => updateSectionField("flowCytometry", "specimenSourceSite", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Date/Time of Collection", false)}
              <TextField
                fullWidth
                size="small"
                type="datetime-local"
                value={
                  clinicalRequirements.flowCytometry?.collectionDateTime ||
                  new Date().toISOString().slice(0, 16)
                }
                onChange={(e) => updateSectionField("flowCytometry", "collectionDateTime", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Collection Tube / Transport Medium", true)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.flowCytometry?.collectionTubeMedium || "Sodium Heparin (Green Top)"}
                  onChange={(e) => updateSectionField("flowCytometry", "collectionTubeMedium", e.target.value)}
                >
                  <MenuItem value="Sodium Heparin (Green Top)">Sodium Heparin (Green Top)</MenuItem>
                  <MenuItem value="RPMI 1640 Medium">RPMI 1640 Transport Medium</MenuItem>
                  <MenuItem value="K2-EDTA (Purple Top)">K2-EDTA (Purple Top)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Specimen Volume", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. 3 mL marrow, 5 mL blood"
                value={clinicalRequirements.flowCytometry?.specimenVolume || "3 mL"}
                onChange={(e) => updateSectionField("flowCytometry", "specimenVolume", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {renderLabel("Transport / Storage Conditions", true)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={
                    clinicalRequirements.flowCytometry?.transportStorageConditions ||
                    "Room Temperature (18-25°C) - DO NOT FREEZE"
                  }
                  onChange={(e) => updateSectionField("flowCytometry", "transportStorageConditions", e.target.value)}
                >
                  <MenuItem value="Room Temperature (18-25°C) - DO NOT FREEZE">
                    Room Temperature (18-25°C) - DO NOT FREEZE
                  </MenuItem>
                  <MenuItem value="2-8°C with Cold Pack (Express Transit)">
                    2-8°C with Cold Pack (Express Transit)
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              {renderLabel("Clinical Indication", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Acute leukemia workup, B-cell lymphoma phenotyping"
                value={clinicalRequirements.flowCytometry?.clinicalIndication || ""}
                onChange={(e) => updateSectionField("flowCytometry", "clinicalIndication", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {renderLabel("Suspected Diagnosis", false)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. B-ALL, CLL, Plasma Cell Myeloma"
                value={clinicalRequirements.flowCytometry?.suspectedDiagnosis || ""}
                onChange={(e) => updateSectionField("flowCytometry", "suspectedDiagnosis", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {renderLabel("Relevant CBC / History", false)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. WBC 45.0, 60% blasts in peripheral blood"
                value={clinicalRequirements.flowCytometry?.cbcHistory || ""}
                onChange={(e) => updateSectionField("flowCytometry", "cbcHistory", e.target.value)}
                sx={inputStyles}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* 2D. Cytogenetics / FISH / Molecular */}
      {activeCategory === "CYTOGENETICS_MOLECULAR" && (
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: textPrimary, mb: 1 }}>
            Cytogenetics / FISH / Molecular Hematopathology Questionnaire
          </Typography>

          <Grid container spacing={1.25}>
            <Grid item xs={12} md={4}>
              {renderLabel("Test / Study Ordered", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Karyotype, BCR-ABL1 FISH, JAK2 V617F, Myeloid NGS"
                value={clinicalRequirements.cytogeneticsMolecular?.testStudyOrdered || ""}
                onChange={(e) => updateSectionField("cytogeneticsMolecular", "testStudyOrdered", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Specimen Type", true)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.cytogeneticsMolecular?.specimenType || "Bone Marrow Aspirate"}
                  onChange={(e) => updateSectionField("cytogeneticsMolecular", "specimenType", e.target.value)}
                >
                  <MenuItem value="Bone Marrow Aspirate">Bone Marrow Aspirate</MenuItem>
                  <MenuItem value="Peripheral Blood">Peripheral Blood</MenuItem>
                  <MenuItem value="FFPE Block">FFPE Block</MenuItem>
                  <MenuItem value="Fresh Tissue">Fresh Tissue</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Specimen Source / Site", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Iliac crest, Venous blood"
                value={clinicalRequirements.cytogeneticsMolecular?.specimenSourceSite || ""}
                onChange={(e) => updateSectionField("cytogeneticsMolecular", "specimenSourceSite", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Date/Time of Collection", false)}
              <TextField
                fullWidth
                size="small"
                type="datetime-local"
                value={
                  clinicalRequirements.cytogeneticsMolecular?.collectionDateTime ||
                  new Date().toISOString().slice(0, 16)
                }
                onChange={(e) => updateSectionField("cytogeneticsMolecular", "collectionDateTime", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Collection Tube / Container", true)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.cytogeneticsMolecular?.collectionTubeContainer || "Sodium Heparin (Green Top)"}
                  onChange={(e) => updateSectionField("cytogeneticsMolecular", "collectionTubeContainer", e.target.value)}
                >
                  <MenuItem value="Sodium Heparin (Green Top)">Sodium Heparin (Green Top - Cytogenetics/FISH)</MenuItem>
                  <MenuItem value="K2-EDTA (Purple Top)">K2-EDTA (Purple Top - Molecular/DNA)</MenuItem>
                  <MenuItem value="FFPE Paraffin Block">FFPE Paraffin Block</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Anticoagulant / Fixative", true)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.cytogeneticsMolecular?.anticoagulantFixative || "Sodium Heparin"}
                  onChange={(e) => updateSectionField("cytogeneticsMolecular", "anticoagulantFixative", e.target.value)}
                >
                  <MenuItem value="Sodium Heparin">Sodium Heparin (No Formalin)</MenuItem>
                  <MenuItem value="K2-EDTA">K2-EDTA</MenuItem>
                  <MenuItem value="10% Neutral Buffered Formalin">10% NBF (For FFPE)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Specimen Volume", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. 3-5 mL"
                value={clinicalRequirements.cytogeneticsMolecular?.specimenVolume || "3 mL"}
                onChange={(e) => updateSectionField("cytogeneticsMolecular", "specimenVolume", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Clinical Indication", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. CML diagnosis, BCR-ABL monitoring"
                value={clinicalRequirements.cytogeneticsMolecular?.clinicalIndication || ""}
                onChange={(e) => updateSectionField("cytogeneticsMolecular", "clinicalIndication", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Suspected Diagnosis", false)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. MPN, CML, MDS"
                value={clinicalRequirements.cytogeneticsMolecular?.suspectedDiagnosis || ""}
                onChange={(e) => updateSectionField("cytogeneticsMolecular", "suspectedDiagnosis", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {renderLabel("Relevant Clinical History", true)}
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="Clinical presentation, previous genetic testing, tyrosine kinase inhibitor therapy..."
                value={clinicalRequirements.cytogeneticsMolecular?.clinicalHistory || ""}
                onChange={(e) => updateSectionField("cytogeneticsMolecular", "clinicalHistory", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {renderLabel("Required Ancillary Studies", false)}
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="Concurrent tests requested: e.g. Karyotype + FISH reflex"
                value={clinicalRequirements.cytogeneticsMolecular?.ancillaryStudies || ""}
                onChange={(e) => updateSectionField("cytogeneticsMolecular", "ancillaryStudies", e.target.value)}
                sx={inputStyles}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* ──────────────────────────────────────────────────────────── */}
      {/* 3. TISSUE PATHOLOGY SECTION                                  */}
      {/* ──────────────────────────────────────────────────────────── */}

      {/* 3A. Surgical Pathology / Biopsy */}
      {activeCategory === "SURGICAL_PATHOLOGY" && (
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: textPrimary, mb: 1 }}>
            Surgical Pathology / Biopsy Questionnaire
          </Typography>

          <Grid container spacing={1.25}>
            <Grid item xs={12} md={4}>
              {renderLabel("Biopsy / Procedure Type", true)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.surgicalBiopsy?.specimenType || "Punch Biopsy"}
                  onChange={(e) => updateSectionField("surgicalBiopsy", "specimenType", e.target.value)}
                >
                  <MenuItem value="Punch Biopsy">Punch Biopsy</MenuItem>
                  <MenuItem value="Endoscopic Biopsy">Endoscopic Biopsy (GI/Bronchial)</MenuItem>
                  <MenuItem value="Core Needle Biopsy">Core Needle Biopsy</MenuItem>
                  <MenuItem value="Excisional Biopsy">Excisional Biopsy</MenuItem>
                  <MenuItem value="Incisional Biopsy">Incisional Biopsy</MenuItem>
                  <MenuItem value="Shave Biopsy">Shave Biopsy</MenuItem>
                  <MenuItem value="Cervical LEEP / Cone">Cervical LEEP / Cone</MenuItem>
                  <MenuItem value="Curettage (Endometrial/Bone)">Curettage</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Anatomic Site", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Skin of back, Gastric antrum, Prostate left base"
                value={clinicalRequirements.surgicalBiopsy?.anatomicSite || ""}
                onChange={(e) => updateSectionField("surgicalBiopsy", "anatomicSite", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Laterality", false)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.surgicalBiopsy?.laterality || "N/A"}
                  onChange={(e) => updateSectionField("surgicalBiopsy", "laterality", e.target.value)}
                >
                  <MenuItem value="Left">Left</MenuItem>
                  <MenuItem value="Right">Right</MenuItem>
                  <MenuItem value="Bilateral">Bilateral</MenuItem>
                  <MenuItem value="Midline">Midline</MenuItem>
                  <MenuItem value="N/A">N/A</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Procedure Performed", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Colonoscopy, Gastroscopy, 4mm punch excision"
                value={clinicalRequirements.surgicalBiopsy?.procedurePerformed || ""}
                onChange={(e) => updateSectionField("surgicalBiopsy", "procedurePerformed", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Date/Time of Collection", false)}
              <TextField
                fullWidth
                size="small"
                type="datetime-local"
                value={
                  clinicalRequirements.surgicalBiopsy?.collectionDateTime ||
                  new Date().toISOString().slice(0, 16)
                }
                onChange={(e) => updateSectionField("surgicalBiopsy", "collectionDateTime", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Clinical Diagnosis / Indication", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Gastric ulcer r/o malignancy, dysplastic nevus"
                value={clinicalRequirements.surgicalBiopsy?.clinicalDiagnosisIndication || ""}
                onChange={(e) => updateSectionField("surgicalBiopsy", "clinicalDiagnosisIndication", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              {renderLabel("Number of Specimens / Containers", true)}
              <TextField
                fullWidth
                size="small"
                type="number"
                inputProps={{ min: 1 }}
                value={clinicalRequirements.surgicalBiopsy?.numberOfContainers || 1}
                onChange={(e) => updateSectionField("surgicalBiopsy", "numberOfContainers", parseInt(e.target.value, 10) || 1)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Fixative", true)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.surgicalBiopsy?.fixative || "10% Neutral Buffered Formalin (10% NBF)"}
                  onChange={(e) => updateSectionField("surgicalBiopsy", "fixative", e.target.value)}
                >
                  <MenuItem value="10% Neutral Buffered Formalin (10% NBF)">10% Neutral Buffered Formalin (10% NBF)</MenuItem>
                  <MenuItem value="Bouin's Fixative">Bouin's Fixative (Testis/GI)</MenuItem>
                  <MenuItem value="Fresh (No Fixative)">Fresh (No Fixative / Fast processing)</MenuItem>
                  <MenuItem value="Glutaraldehyde (Electron Microscopy)">Glutaraldehyde (EM)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={5}>
              {renderLabel("Fixation Start Time", false)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Placed in formalin at 11:30 AM"
                value={clinicalRequirements.surgicalBiopsy?.fixationStartTime || ""}
                onChange={(e) => updateSectionField("surgicalBiopsy", "fixationStartTime", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {renderLabel("Relevant Clinical History", true)}
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="Symptoms duration, prior biopsies, immunosuppression..."
                value={clinicalRequirements.surgicalBiopsy?.clinicalHistory || ""}
                onChange={(e) => updateSectionField("surgicalBiopsy", "clinicalHistory", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              {renderLabel("Operative Findings", false)}
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="Lesion visual characteristics, ulceration, depth..."
                value={clinicalRequirements.surgicalBiopsy?.operativeFindings || ""}
                onChange={(e) => updateSectionField("surgicalBiopsy", "operativeFindings", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              {renderLabel("Special Studies Requested", false)}
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="e.g. H. pylori, PAS, fungal stain, cytokeratin"
                value={clinicalRequirements.surgicalBiopsy?.specialStudiesRequested || ""}
                onChange={(e) => updateSectionField("surgicalBiopsy", "specialStudiesRequested", e.target.value)}
                sx={inputStyles}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* 3B. Resection Specimens */}
      {activeCategory === "RESECTION" && (
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: textPrimary, mb: 1 }}>
            Resection Specimens Questionnaire (Major Organs & Resections)
          </Typography>

          <Grid container spacing={1.25}>
            <Grid item xs={12} md={4}>
              {renderLabel("Surgical Resection / Procedure Type", true)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.resectionSpecimen?.specimenType || "Colectomy / Hemicolectomy"}
                  onChange={(e) => updateSectionField("resectionSpecimen", "specimenType", e.target.value)}
                >
                  <MenuItem value="Colectomy / Hemicolectomy">Colectomy / Hemicolectomy</MenuItem>
                  <MenuItem value="Mastectomy">Mastectomy</MenuItem>
                  <MenuItem value="Gastrectomy">Gastrectomy</MenuItem>
                  <MenuItem value="Thyroidectomy">Thyroidectomy</MenuItem>
                  <MenuItem value="Nephrectomy">Nephrectomy</MenuItem>
                  <MenuItem value="Prostatectomy">Prostatectomy</MenuItem>
                  <MenuItem value="Hysterectomy & BSO">Hysterectomy & Salpingo-Oophorectomy</MenuItem>
                  <MenuItem value="Limb Amputation">Limb Amputation</MenuItem>
                  <MenuItem value="Other Organ Resection">Other Organ Resection</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Anatomic Site", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Sigmoid colon, Right kidney, Uterus and ovaries"
                value={clinicalRequirements.resectionSpecimen?.anatomicSite || ""}
                onChange={(e) => updateSectionField("resectionSpecimen", "anatomicSite", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Laterality", false)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.resectionSpecimen?.laterality || "N/A"}
                  onChange={(e) => updateSectionField("resectionSpecimen", "laterality", e.target.value)}
                >
                  <MenuItem value="Left">Left</MenuItem>
                  <MenuItem value="Right">Right</MenuItem>
                  <MenuItem value="Bilateral">Bilateral</MenuItem>
                  <MenuItem value="Midline">Midline</MenuItem>
                  <MenuItem value="N/A">N/A</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Surgical Procedure", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Laparoscopic anterior resection, Radical nephrectomy"
                value={clinicalRequirements.resectionSpecimen?.surgicalProcedure || ""}
                onChange={(e) => updateSectionField("resectionSpecimen", "surgicalProcedure", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Date/Time of Collection", false)}
              <TextField
                fullWidth
                size="small"
                type="datetime-local"
                value={
                  clinicalRequirements.resectionSpecimen?.collectionDateTime ||
                  new Date().toISOString().slice(0, 16)
                }
                onChange={(e) => updateSectionField("resectionSpecimen", "collectionDateTime", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Clinical Diagnosis", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Adenocarcinoma of colon, Renal cell carcinoma"
                value={clinicalRequirements.resectionSpecimen?.clinicalDiagnosis || ""}
                onChange={(e) => updateSectionField("resectionSpecimen", "clinicalDiagnosis", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              {renderLabel("Number of Specimens / Containers", true)}
              <TextField
                fullWidth
                size="small"
                type="number"
                inputProps={{ min: 1 }}
                value={clinicalRequirements.resectionSpecimen?.numberOfContainers || 1}
                onChange={(e) => updateSectionField("resectionSpecimen", "numberOfContainers", parseInt(e.target.value, 10) || 1)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Fixative", true)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.resectionSpecimen?.fixative || "10% Neutral Buffered Formalin"}
                  onChange={(e) => updateSectionField("resectionSpecimen", "fixative", e.target.value)}
                >
                  <MenuItem value="10% Neutral Buffered Formalin">10% Neutral Buffered Formalin (10x volume)</MenuItem>
                  <MenuItem value="Fresh (For immediate grossing/margin inking)">Fresh (For immediate grossing/margin inking)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={5}>
              {renderLabel("Orientation Information", false, "Suture markers and ink designations")}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Short stitch = Superior; Long stitch = Lateral; Deep margin inked"
                value={clinicalRequirements.resectionSpecimen?.orientationInfo || ""}
                onChange={(e) => updateSectionField("resectionSpecimen", "orientationInfo", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {renderLabel("Relevant Clinical History", true)}
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="Prior neo-adjuvant chemoradiation, clinical stage, TNM..."
                value={clinicalRequirements.resectionSpecimen?.clinicalHistory || ""}
                onChange={(e) => updateSectionField("resectionSpecimen", "clinicalHistory", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              {renderLabel("Operative Findings", false)}
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="Peritoneal washings, tumor extension, lymph nodes..."
                value={clinicalRequirements.resectionSpecimen?.operativeFindings || ""}
                onChange={(e) => updateSectionField("resectionSpecimen", "operativeFindings", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              {renderLabel("Special Studies Requested", false)}
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="e.g. MMR/MSI panel, HER2, PD-L1, KRAS"
                value={clinicalRequirements.resectionSpecimen?.specialStudiesRequested || ""}
                onChange={(e) => updateSectionField("resectionSpecimen", "specialStudiesRequested", e.target.value)}
                sx={inputStyles}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* 3C. Breast Pathology */}
      {activeCategory === "BREAST_PATHOLOGY" && (
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: textPrimary, mb: 1 }}>
            Breast Pathology Questionnaire (Biopsies & Lumpectomies)
          </Typography>

          <Grid container spacing={1.25}>
            <Grid item xs={12} md={4}>
              {renderLabel("Biopsy / Procedure Type", true)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.breastPathology?.specimenType || "Core Needle Biopsy"}
                  onChange={(e) => updateSectionField("breastPathology", "specimenType", e.target.value)}
                >
                  <MenuItem value="Core Needle Biopsy">Core Needle Biopsy</MenuItem>
                  <MenuItem value="Vacuum-Assisted Biopsy (VAB)">Vacuum-Assisted Biopsy (VAB)</MenuItem>
                  <MenuItem value="Lumpectomy / Wide Local Excision">Lumpectomy / Wide Local Excision</MenuItem>
                  <MenuItem value="Total Mastectomy">Total Mastectomy</MenuItem>
                  <MenuItem value="Sentinel Lymph Node">Sentinel Lymph Node</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Breast / Site", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Upper outer quadrant, 2 o'clock, 3cm from nipple"
                value={clinicalRequirements.breastPathology?.breastSite || ""}
                onChange={(e) => updateSectionField("breastPathology", "breastSite", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Laterality", false)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.breastPathology?.laterality || "Right"}
                  onChange={(e) => updateSectionField("breastPathology", "laterality", e.target.value)}
                >
                  <MenuItem value="Left">Left</MenuItem>
                  <MenuItem value="Right">Right</MenuItem>
                  <MenuItem value="Bilateral">Bilateral</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              {renderLabel("Procedure", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Wire-guided lumpectomy, Ultrasound core biopsy"
                value={clinicalRequirements.breastPathology?.procedure || ""}
                onChange={(e) => updateSectionField("breastPathology", "procedure", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {renderLabel("Clinical Indication", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Palpable mass, BI-RADS 5 microcalcifications"
                value={clinicalRequirements.breastPathology?.clinicalIndication || ""}
                onChange={(e) => updateSectionField("breastPathology", "clinicalIndication", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Fixative", true)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.breastPathology?.fixative || "10% Neutral Buffered Formalin (ASCO/CAP compliant)"}
                  onChange={(e) => updateSectionField("breastPathology", "fixative", e.target.value)}
                >
                  <MenuItem value="10% Neutral Buffered Formalin (ASCO/CAP compliant)">
                    10% Neutral Buffered Formalin (ASCO/CAP compliant)
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Cold Ischemia Time", false, "Time from tissue devascularization to formalin immersion (<1 hour recommended)")}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. 25 minutes (< 60 mins)"
                value={clinicalRequirements.breastPathology?.coldIschemiaTime || ""}
                onChange={(e) => updateSectionField("breastPathology", "coldIschemiaTime", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Fixation Duration", false, "6 to 72 hours per ASCO/CAP")}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. 24 hours (6-72 hrs recommended)"
                value={clinicalRequirements.breastPathology?.fixationDuration || ""}
                onChange={(e) => updateSectionField("breastPathology", "fixationDuration", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {renderLabel("Relevant Clinical History", true)}
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="Menopausal status, prior breast cancer, family history, BRCA status, neo-adjuvant chemo..."
                value={clinicalRequirements.breastPathology?.clinicalHistory || ""}
                onChange={(e) => updateSectionField("breastPathology", "clinicalHistory", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              {renderLabel("Imaging Findings", false)}
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="BI-RADS score, mass dimensions, calcifications..."
                value={clinicalRequirements.breastPathology?.imagingFindings || ""}
                onChange={(e) => updateSectionField("breastPathology", "imagingFindings", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              {renderLabel("Special Studies / IHC / ISH", false)}
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="ER, PR, HER2 IHC, Ki-67..."
                value={clinicalRequirements.breastPathology?.specialStudiesRequested || ""}
                onChange={(e) => updateSectionField("breastPathology", "specialStudiesRequested", e.target.value)}
                sx={inputStyles}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* 3D. Special Stains / IHC */}
      {activeCategory === "SPECIAL_STAINS_IHC" && (
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: textPrimary, mb: 1 }}>
            Special Stains / Immunohistochemistry (IHC) Request Questionnaire
          </Typography>

          <Grid container spacing={1.25}>
            <Grid item xs={12} md={4}>
              {renderLabel("Test / Stain Requested", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. IHC Panel, D-PAS, AFB Stain, Iron Stain"
                value={clinicalRequirements.specialStainsIHC?.testStainRequested || ""}
                onChange={(e) => updateSectionField("specialStainsIHC", "testStainRequested", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Specimen Type", true)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.specialStainsIHC?.specimenType || "FFPE Paraffin Block"}
                  onChange={(e) => updateSectionField("specialStainsIHC", "specimenType", e.target.value)}
                >
                  <MenuItem value="FFPE Paraffin Block">FFPE Paraffin Block</MenuItem>
                  <MenuItem value="Unstained Charged Slides">Unstained Charged Slides</MenuItem>
                  <MenuItem value="Cytology Cell Block">Cytology Cell Block</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Anatomic Site", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Lung core biopsy, Cervical lymph node"
                value={clinicalRequirements.specialStainsIHC?.anatomicSite || ""}
                onChange={(e) => updateSectionField("specialStainsIHC", "anatomicSite", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Specimen / Block / Slide Identification", true, "Exact block number / cassette ID")}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. S24-1049 Block A2, Slide #4"
                value={clinicalRequirements.specialStainsIHC?.blockSlideId || ""}
                onChange={(e) => updateSectionField("specialStainsIHC", "blockSlideId", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Requested Marker / Stain", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. CK7, CK20, TTF-1, p40, CD3, CD20, Ki-67"
                value={clinicalRequirements.specialStainsIHC?.requestedMarkerStain || ""}
                onChange={(e) => updateSectionField("specialStainsIHC", "requestedMarkerStain", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Clinical Indication", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Differentiate primary lung adenocarcinoma vs squamous"
                value={clinicalRequirements.specialStainsIHC?.clinicalIndication || ""}
                onChange={(e) => updateSectionField("specialStainsIHC", "clinicalIndication", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Relevant Pathology History", false)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Primary breast cancer diagnosed 2021"
                value={clinicalRequirements.specialStainsIHC?.pathologyHistory || ""}
                onChange={(e) => updateSectionField("specialStainsIHC", "pathologyHistory", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Previous Diagnosis", false)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Poorly differentiated carcinoma"
                value={clinicalRequirements.specialStainsIHC?.previousDiagnosis || ""}
                onChange={(e) => updateSectionField("specialStainsIHC", "previousDiagnosis", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Control Requirements", false)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. On-slide positive control tissue required"
                value={clinicalRequirements.specialStainsIHC?.controlRequirements || ""}
                onChange={(e) => updateSectionField("specialStainsIHC", "controlRequirements", e.target.value)}
                sx={inputStyles}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* 3E. Frozen Section / Intraoperative */}
      {activeCategory === "FROZEN_SECTION" && (
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: textPrimary, mb: 1 }}>
            Frozen Section / Intraoperative Consultation Questionnaire
          </Typography>

          <Grid container spacing={1.25}>
            <Grid item xs={12} md={4}>
              {renderLabel("Specimen Type", true)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.frozenSection?.specimenType || "Surgical Margin Shave"}
                  onChange={(e) => updateSectionField("frozenSection", "specimenType", e.target.value)}
                >
                  <MenuItem value="Surgical Margin Shave">Surgical Margin Shave</MenuItem>
                  <MenuItem value="Sentinel Lymph Node">Sentinel Lymph Node</MenuItem>
                  <MenuItem value="Ovarian Mass / Cyst">Ovarian Mass / Cyst</MenuItem>
                  <MenuItem value="Thyroid / Parathyroid Tissue">Thyroid / Parathyroid Tissue</MenuItem>
                  <MenuItem value="Biopsy for Diagnostic Adequacy">Biopsy for Diagnostic Adequacy</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Anatomic Site", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Bile duct resection margin, Axillary sentinel node"
                value={clinicalRequirements.frozenSection?.anatomicSite || ""}
                onChange={(e) => updateSectionField("frozenSection", "anatomicSite", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Laterality", false)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={clinicalRequirements.frozenSection?.laterality || "N/A"}
                  onChange={(e) => updateSectionField("frozenSection", "laterality", e.target.value)}
                >
                  <MenuItem value="Left">Left</MenuItem>
                  <MenuItem value="Right">Right</MenuItem>
                  <MenuItem value="Bilateral">Bilateral</MenuItem>
                  <MenuItem value="Midline">Midline</MenuItem>
                  <MenuItem value="N/A">N/A</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Surgical Procedure", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Whipple procedure, Partial mastectomy"
                value={clinicalRequirements.frozenSection?.surgicalProcedure || ""}
                onChange={(e) => updateSectionField("frozenSection", "surgicalProcedure", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Date/Time Collected", false)}
              <TextField
                fullWidth
                size="small"
                type="datetime-local"
                value={
                  clinicalRequirements.frozenSection?.collectionDateTime ||
                  new Date().toISOString().slice(0, 16)
                }
                onChange={(e) => updateSectionField("frozenSection", "collectionDateTime", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Clinical Question / Indication", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Margin clearance, benign vs malignant check"
                value={clinicalRequirements.frozenSection?.clinicalQuestionIndication || ""}
                onChange={(e) => updateSectionField("frozenSection", "clinicalQuestionIndication", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Intraoperative Urgency", true)}
              <FormControl fullWidth size="small" sx={inputStyles}>
                <Select
                  value={
                    clinicalRequirements.frozenSection?.intraoperativeUrgency ||
                    "STAT - Patient Under General Anesthesia"
                  }
                  onChange={(e) => updateSectionField("frozenSection", "intraoperativeUrgency", e.target.value)}
                >
                  <MenuItem value="STAT - Patient Under General Anesthesia">
                    STAT - Patient Under General Anesthesia (15-20 min target)
                  </MenuItem>
                  <MenuItem value="URGENT - Awaiting Margin Clearance Before Closure">
                    URGENT - Awaiting Margin Clearance Before Closure
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Surgeon / Ordering Clinician", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Dr. A. Adebayo (Operating Surgeon)"
                value={clinicalRequirements.frozenSection?.surgeonOrderingClinician || ""}
                onChange={(e) => updateSectionField("frozenSection", "surgeonOrderingClinician", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel("Operating Room / Location", true)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. OR 2, Main Surgical Block (Ext. 4102)"
                value={clinicalRequirements.frozenSection?.operatingRoomLocation || ""}
                onChange={(e) => updateSectionField("frozenSection", "operatingRoomLocation", e.target.value)}
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12}>
              {renderLabel("Special Instructions", false)}
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Call OR direct line immediately with preliminary diagnosis; send remainder for permanent sections"
                value={clinicalRequirements.frozenSection?.specialInstructions || ""}
                onChange={(e) => updateSectionField("frozenSection", "specialInstructions", e.target.value)}
                sx={inputStyles}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* ──────────────────────────────────────────────────────────── */}
      {/* MULTIPLE SPECIMENS SUPPORT (General LIS Notes)               */}
      {/* ──────────────────────────────────────────────────────────── */}
      <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px dashed ${borderColor}` }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: textPrimary }}>
              Multiple Specimens / Containers Tracking
            </Typography>
            <Typography sx={{ fontSize: "0.68rem", color: textSecondary }}>
              Support multiple specimens/jars under this order (e.g. Specimen A: Polyp, Specimen B: Random biopsy)
            </Typography>
          </Box>

          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={handleAddSpecimenContainer}
            sx={{
              textTransform: "none",
              fontFamily: "'Satoshi', sans-serif",
              fontSize: "0.78rem",
              fontWeight: 700,
              borderRadius: "7px",
              borderColor: "#3B82F6",
              color: "#2563EB",
            }}
          >
            Add Specimen Container
          </Button>
        </Box>

        {specimensList.length > 0 ? (
          <Stack spacing={1.5}>
            {specimensList.map((sp, idx) => (
              <Paper
                key={idx}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: "10px",
                  bgcolor: innerCardBg,
                  border: `1px solid ${borderColor}`,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.76rem", color: "#2563EB" }}>
                    Container #{idx + 1}
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveSpecimenContainer(idx)}
                    sx={{ p: 0.5 }}
                  >
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>

                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Container Label / Jar ID"
                      value={sp.containerLabel || ""}
                      onChange={(e) => handleUpdateSpecimenField(idx, "containerLabel", e.target.value)}
                      sx={inputStyles}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Anatomic Site / Source"
                      placeholder="e.g. Sigmoid polyp"
                      value={sp.anatomicSite || ""}
                      onChange={(e) => handleUpdateSpecimenField(idx, "anatomicSite", e.target.value)}
                      sx={inputStyles}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small" sx={inputStyles}>
                      <InputLabel>Fixative</InputLabel>
                      <Select
                        label="Fixative"
                        value={sp.fixative || "10% Neutral Buffered Formalin"}
                        onChange={(e) => handleUpdateSpecimenField(idx, "fixative", e.target.value)}
                      >
                        <MenuItem value="10% Neutral Buffered Formalin">10% Neutral Buffered Formalin</MenuItem>
                        <MenuItem value="Fresh (Unfixed)">Fresh (Unfixed)</MenuItem>
                        <MenuItem value="95% Ethyl Alcohol">95% Ethyl Alcohol</MenuItem>
                        <MenuItem value="CytoLyt Solution">CytoLyt Solution</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Volume / Pieces"
                      placeholder="e.g. 1 piece, 3 cores"
                      value={sp.volume || ""}
                      onChange={(e) => handleUpdateSpecimenField(idx, "volume", e.target.value)}
                      sx={inputStyles}
                    />
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Stack>
        ) : (
          <Box
            sx={{
              py: 2,
              px: 3,
              borderRadius: "8px",
              bgcolor: innerCardBg,
              border: `1px dashed ${borderColor}`,
              textAlign: "center",
            }}
          >
            <Typography sx={{ fontSize: "0.8rem", color: textSecondary }}>
              Single specimen order by default. If this procedure generated multiple jars/containers, click <b>"Add Specimen Container"</b> above.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Validation Errors Alert - ultra-slim inline text without bulky side icons */}
      {validationErrors && validationErrors.length > 0 && (
        <Box
          sx={{
            mt: 1,
            py: 0.35,
            px: 1.25,
            borderRadius: "6px",
            bgcolor: isDark ? "rgba(239, 68, 68, 0.12)" : "#FEF2F2",
            border: "1px solid #FCA5A5",
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            flexWrap: "wrap",
          }}
        >
          <Typography component="span" sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#DC2626" }}>
            Required:
          </Typography>
          <Typography component="span" sx={{ fontSize: "0.75rem", color: "#EF4444", fontWeight: 500 }}>
            {validationErrors.join(" • ")}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
